import type { ReactNode } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SendIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import type { JSONContent } from "@tiptap/react";
import { z } from "zod";

import { Column, MentionTextInput, Row } from "@/components";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";

import { tiptapDefaultJson } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { tiptapContentJsonSchema } from "@/lib/schemas";

const composerSchema = z.object({
  contentJson: tiptapContentJsonSchema,
});

// Shared comms-rail composer: mention-capable input, a scope banner along the
// bottom, and a round send button. Callers own the mutation; they call the
// provided reset once the send succeeds. The draft is lifted to the panel so
// it survives scope/tab switches unmounting the composer.
export const CommentComposer = ({
  banner,
  draft,
  isPending,
  onDraftChange,
  onSend,
  placeholder,
}: {
  banner: ReactNode;
  draft: JSONContent;
  isPending: boolean;
  onDraftChange: (contentJson: JSONContent) => void;
  onSend: (contentJson: JSONContent, reset: () => void) => void;
  placeholder: string;
}) => {
  /* React Hooks */
  const form = useForm<z.infer<typeof composerSchema>>({
    // Seed from the lifted draft so the text survives tab switches.
    defaultValues: { contentJson: draft as { type: "doc"; content: unknown[] } },
    resolver: zodResolver(composerSchema),
  });

  /* Helpers */
  const canSend =
    !isPending &&
    JSON.stringify(form.watch("contentJson").content) !==
      JSON.stringify(tiptapDefaultJson.content);

  /* Handlers */
  const handleSubmit = (data: z.infer<typeof composerSchema>) => {
    if (isPending) return;

    onSend(data.contentJson, () => {
      form.reset({ contentJson: tiptapDefaultJson });
      onDraftChange(tiptapDefaultJson);
    });
  };

  /* Render */
  const render = () => {
    return (
      <Form {...form}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <Column className="w-full gap-3 pt-3">
            <FormField
              control={form.control}
              name="contentJson"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <MentionTextInput
                      className="border-slate-300/70 bg-primary-50"
                      onChange={(contentJson) => {
                        field.onChange(contentJson);
                        onDraftChange(contentJson);
                      }}
                      onSubmit={() => {
                        mixpanelUtils.trackKeyboardShortcutUsed(
                          "Cmd+Enter (Add Comment)",
                          MIXPANEL_SCREENS.CANDIDATE_SHEET,
                        );
                        void form.handleSubmit(handleSubmit)();
                      }}
                      placeholder={placeholder}
                      value={field.value}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <Row className="items-center gap-2">
              <Row className="min-w-0 flex-1 shrink-0 items-center gap-2 text-slate-500">
                {banner}
              </Row>
              <Button
                className="h-9 w-9 shrink-0 rounded-full"
                disabled={!canSend}
                onMouseDown={(e) => {
                  // Keep keyboard focus in the input when clicking send.
                  e.preventDefault();
                }}
                size="icon"
                type="submit"
              >
                <SendIcon className="h-4 w-4" />
              </Button>
            </Row>
          </Column>
        </form>
      </Form>
    );
  };

  return render();
};
